import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export { supabase }

export function useSupabaseQuery(table, options = {}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  async function fetchData() {
    // Skip fetch if options is null (used for conditional queries)
    if (options === null) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      
      let query = supabase.from(table).select(options.select || '*')
      
      if (options.eq) {
        Object.entries(options.eq).forEach(([key, value]) => {
          query = query.eq(key, value)
        })
      }
      
      if (options.order) {
        query = query.order(options.order.column, { ascending: options.order.ascending ?? true })
      }

      if (options.limit) {
        query = query.limit(options.limit)
      }
      
      const { data: result, error: err } = await query
      
      if (err) throw err
      
      setData(result)
      setError(null)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // Use a stable dependency - only refetch when table or stringified options change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, options?.eq ? JSON.stringify(options.eq) : '', options?.order ? JSON.stringify(options.order) : '', options?.limit, options?.select])

  // Set up real-time subscription for automatic updates
  useEffect(() => {
    const channel = supabase
      .channel(`${table}-changes`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table
        },
        (payload) => {
          fetchData() // Auto-refresh when data changes
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [table])

  return { data, loading, error, refetch: fetchData }
}

export function useSupabaseRpc(fn, params, options = {}) {
  const enabled = options.enabled !== false
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState(null)

  async function fetchData() {
    if (!enabled) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const { data: result, error: err } = await supabase.rpc(fn, params)
      if (err) throw err
      setData(result)
      setError(null)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // Re-run when the fn name or stringified params change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn, JSON.stringify(params ?? {}), enabled])

  return { data, loading, error, refetch: fetchData }
}

export function useSupabaseMutation(table) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  async function insert(data) {
    setLoading(true)
    setError(null)
    try {
      const { data: result, error: err } = await supabase
        .from(table)
        .insert(data)
        .select()
        .single()
      
      if (err) throw err
      return { data: result, error: null }
    } catch (err) {
      setError(err)
      return { data: null, error: err }
    } finally {
      setLoading(false)
    }
  }

  async function update(id, data, idColumn = 'id') {
    setLoading(true)
    setError(null)
    try {
      const { data: result, error: err } = await supabase
        .from(table)
        .update(data)
        .eq(idColumn, id)
        .select()
        .single()
      
      if (err) throw err
      return { data: result, error: null }
    } catch (err) {
      setError(err)
      return { data: null, error: err }
    } finally {
      setLoading(false)
    }
  }

  async function remove(id, idColumn) {
    setLoading(true)
    setError(null)
    try {
      const { error: err } = await supabase
        .from(table)
        .delete()
        .eq(idColumn, id)
      
      if (err) throw err
      return { error: null }
    } catch (err) {
      setError(err)
      return { error: err }
    } finally {
      setLoading(false)
    }
  }

  async function softDelete(id, idColumn = 'id') {
    return update(id, { IsDeleted: true }, idColumn)
  }

  return { insert, update, remove, softDelete, loading, error }
}
